import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, User, Mail, Lock, ChevronRight, ArrowLeft, Factory, Shield, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TenantSignupPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sectors, setSectors] = useState([]);
  
  const [formData, setFormData] = useState({
    company_name: '',
    company_slug: '',
    company_email: '',
    sector_id: '',
    admin_name: '',
    admin_email: '',
    admin_password: '',
    admin_password_confirm: ''
  });

  useEffect(() => {
    fetchSectors();
  }, []);

  const fetchSectors = async () => {
    try {
      const res = await fetch(`${API_URL}/api/public/sectors`);
      if (res.ok) {
        const data = await res.json();
        setSectors(data);
      }
    } catch (err) {
      console.error('Failed to fetch sectors:', err);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      if (name === 'company_name') {
        newData.company_slug = generateSlug(value);
      }
      return newData;
    });
  };

  const selectSector = (sectorId) => {
    setFormData(prev => ({ ...prev, sector_id: sectorId }));
  };

  const validateStep1 = () => {
    if (!formData.company_name || !formData.company_slug || !formData.company_email) {
      toast.error('Lütfen tüm şirket bilgilerini doldurun');
      return false;
    }
    if (!formData.company_email.includes('@')) {
      toast.error('Geçerli bir e-posta adresi girin');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.sector_id) {
      toast.error('Lütfen bir sektör seçin');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.admin_name || !formData.admin_email || !formData.admin_password) {
      toast.error('Lütfen tüm yönetici bilgilerini doldurun');
      return false;
    }
    if (formData.admin_password !== formData.admin_password_confirm) {
      toast.error('Şifreler eşleşmiyor');
      return false;
    }
    if (formData.admin_password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalı');
      return false;
    }
    return true;
  };

  const nextStep = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep3()) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/signup/tenant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: formData.company_name,
          company_slug: formData.company_slug,
          company_email: formData.company_email,
          sector_id: formData.sector_id,
          admin_name: formData.admin_name,
          admin_email: formData.admin_email,
          admin_password: formData.admin_password
        })
      });

      const data = await res.json();

      if (res.ok) {
        // Store token and redirect
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        toast.success('Şirket hesabınız oluşturuldu!');
        navigate('/dashboard');
      } else {
        toast.error(data.detail || 'Kayıt başarısız');
      }
    } catch (err) {
      toast.error('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const selectedSector = sectors.find(s => s.id === formData.sector_id);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-slate-800 to-background">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMDIwMjAiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnYtMmgtNHYyaC0ydjJoMnY0aDJ2Mmg0di0yaC0ydi00eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />
      
      <div className="relative min-h-screen flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-2xl">
          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <Link to="/login" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Giriş sayfasına dön
            </Link>
            <h1 className="text-3xl font-bold text-foreground mb-2">Şirket Hesabı Oluştur</h1>
            <p className="text-muted-foreground">
              UyumAkademi SaaS platformuna şirketinizi kaydedin
            </p>
          </motion.div>

          {/* Progress Steps */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center space-x-4">
              {[1, 2, 3].map((s) => (
                <React.Fragment key={s}>
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${
                    step >= s 
                      ? 'bg-primary border-primary text-foreground' 
                      : 'border-slate-600 text-muted-foreground'
                  }`}>
                    {step > s ? <Check className="w-5 h-5" /> : s}
                  </div>
                  {s < 3 && (
                    <div className={`w-16 h-0.5 ${step > s ? 'bg-primary' : 'bg-slate-600'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Form Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-secondary backdrop-blur-xl rounded-2xl border border-border p-8"
          >
            <form onSubmit={handleSubmit}>
              {/* Step 1: Company Info */}
              {step === 1 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 rounded-xl bg-primary/10">
                      <Building2 className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-foreground">Şirket Bilgileri</h2>
                      <p className="text-sm text-muted-foreground">Şirketinizin temel bilgilerini girin</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="company_name" className="text-foreground">Şirket Adı</Label>
                      <Input
                        id="company_name"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        placeholder="Örn: ABC Finans A.Ş."
                        className="bg-secondary border-border text-foreground placeholder:text-muted-foreground"
                        data-testid="company-name-input"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="company_slug" className="text-foreground">Şirket URL</Label>
                      <div className="flex items-center">
                        <span className="text-muted-foreground text-sm mr-2">uyumakademi.com/</span>
                        <Input
                          id="company_slug"
                          name="company_slug"
                          value={formData.company_slug}
                          onChange={handleChange}
                          placeholder="abc-finans"
                          className="bg-secondary border-border text-foreground placeholder:text-muted-foreground"
                          data-testid="company-slug-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="company_email" className="text-foreground">Şirket E-postası</Label>
                      <Input
                        id="company_email"
                        name="company_email"
                        type="email"
                        value={formData.company_email}
                        onChange={handleChange}
                        placeholder="info@sirket.com"
                        className="bg-secondary border-border text-foreground placeholder:text-muted-foreground"
                        data-testid="company-email-input"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Sector Selection */}
              {step === 2 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 rounded-xl bg-amber-500/10">
                      <Factory className="w-6 h-6 text-amber-500" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-foreground">Sektör Seçimi</h2>
                      <p className="text-sm text-muted-foreground">Şirketinizin faaliyet gösterdiği sektörü seçin</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {sectors.map((sector) => (
                      <button
                        key={sector.id}
                        type="button"
                        onClick={() => selectSector(sector.id)}
                        className={`p-4 rounded-xl border-2 transition-all text-left ${
                          formData.sector_id === sector.id
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/30 bg-secondary'
                        }`}
                        data-testid={`sector-${sector.code}`}
                      >
                        <div className="text-2xl mb-2">{sector.icon}</div>
                        <div className="font-medium text-foreground">{sector.name}</div>
                        <div className="text-xs text-muted-foreground mt-1">{sector.description}</div>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 3: Admin Account */}
              {step === 3 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 rounded-xl bg-green-500/10">
                      <Shield className="w-6 h-6 text-green-500" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-foreground">Yönetici Hesabı</h2>
                      <p className="text-sm text-muted-foreground">Şirket yöneticisi hesabı oluşturun</p>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="p-4 rounded-xl bg-secondary border border-border mb-6">
                    <div className="text-sm text-muted-foreground mb-1">Oluşturulacak şirket:</div>
                    <div className="font-medium text-foreground">{formData.company_name}</div>
                    <div className="text-sm text-muted-foreground mt-2">Sektör: {selectedSector?.name}</div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="admin_name" className="text-foreground">Ad Soyad</Label>
                      <Input
                        id="admin_name"
                        name="admin_name"
                        value={formData.admin_name}
                        onChange={handleChange}
                        placeholder="Yönetici adı"
                        className="bg-secondary border-border text-foreground placeholder:text-muted-foreground"
                        data-testid="admin-name-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="admin_email" className="text-foreground">E-posta</Label>
                      <Input
                        id="admin_email"
                        name="admin_email"
                        type="email"
                        value={formData.admin_email}
                        onChange={handleChange}
                        placeholder="yonetici@sirket.com"
                        className="bg-secondary border-border text-foreground placeholder:text-muted-foreground"
                        data-testid="admin-email-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="admin_password" className="text-foreground">Şifre</Label>
                        <Input
                          id="admin_password"
                          name="admin_password"
                          type="password"
                          value={formData.admin_password}
                          onChange={handleChange}
                          placeholder="••••••••"
                          className="bg-secondary border-border text-foreground placeholder:text-muted-foreground"
                          data-testid="admin-password-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="admin_password_confirm" className="text-foreground">Şifre Tekrar</Label>
                        <Input
                          id="admin_password_confirm"
                          name="admin_password_confirm"
                          type="password"
                          value={formData.admin_password_confirm}
                          onChange={handleChange}
                          placeholder="••••••••"
                          className="bg-secondary border-border text-foreground placeholder:text-muted-foreground"
                          data-testid="admin-password-confirm-input"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8">
                {step > 1 ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={prevStep}
                    className="border-border text-foreground hover:bg-muted"
                    data-testid="prev-step-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Geri
                  </Button>
                ) : (
                  <div />
                )}

                {step < 3 ? (
                  <Button
                    type="button"
                    onClick={nextStep}
                    className="bg-primary hover:bg-primary/90"
                    data-testid="next-step-btn"
                  >
                    İleri
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="submit-signup-btn"
                  >
                    {loading ? 'Oluşturuluyor...' : 'Hesabı Oluştur'}
                  </Button>
                )}
              </div>
            </form>
          </motion.div>

          {/* Footer Info */}
          <div className="text-center mt-6 text-sm text-muted-foreground">
            Hesap oluşturarak{' '}
            <a href="#" className="text-primary hover:underline">Kullanım Şartları</a>
            {' '}ve{' '}
            <a href="#" className="text-primary hover:underline">Gizlilik Politikası</a>'nı kabul etmiş olursunuz.
          </div>

          {/* Free Tier Info */}
          <div className="mt-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-amber-500 mt-0.5" />
              <div>
                <div className="font-medium text-foreground">Ücretsiz Başlangıç</div>
                <div className="text-sm text-muted-foreground">
                  3 kullanıcıya kadar ücretsiz kullanabilirsiniz. Daha fazla kullanıcı için ücretli planlara geçebilirsiniz.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
